import React, { useEffect } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  useDisclosure,
  DrawerFooter,
  DrawerCloseButton
} from "@chakra-ui/react";

import { useScreenViewport } from "lib/checkViewport";
import type { ProductCardProduct, VariantType } from "@shared/graphql.types";

import { useHasMounted } from "utils/wrappers";
import { addToBagModalOpenVar } from "lib/apollo/variables";

import { useSegment } from "tracking";
import { withErrorBoundary } from "react-error-boundary";
import { Logger } from "lib/logger";
import { useNotifications } from "@shared/utils.notifications";
import { SuccessIcon } from "components/SvgIcons";
import { useChatVisibility } from "hooks";
import { titleContainer, title } from "./ATBConfirmation.css";
import { DrawerFooterWrapper } from "../Drawer/DrawerFooterWrapper";
import { ConfirmationPage } from "./ConfirmationPage";
import { DeprecatedDrawerLoadingMask } from "../Drawer/DrawerLoadingMask";
import { ProductAddedBanner } from "../ProductCard/ProductAddedBanner/ProductAddedBanner";
import { useATBConfirmationContext } from "./context/ATBConfirmationContext";
import { shadows } from "@shared/ui.design-system";

export type ModalCloseType =
  | "Close Outside"
  | "Close Icon"
  | "Continue Shopping CTA";

type Props = {
  confirmation: boolean;
  variant?: VariantType | ProductCardProduct | { sku: string };
  variants?: VariantType[];
  setConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
  source: "PDP" | "PLP" | "Search" | "LandingPage" | "Quiz";
  isFreeTrialVariant?: boolean;
};

const ATBConfirmationComp: React.FC<Props> = ({
  confirmation,
  variant = undefined,
  variants,
  setConfirmation,
  isFreeTrialVariant = false
}) => {
  const { track } = useSegment();
  const { isATBLoading, showProductAddedBanner } = useATBConfirmationContext();
  const { onClose } = useDisclosure();
  const { globalNotifications: notifications } = useNotifications();
  const hasMounted = useHasMounted();
  useChatVisibility(confirmation);
  const { isDesktop, isExtraLarge } = useScreenViewport();

  useEffect(() => {
    if (notifications.length > 0) {
      setConfirmation(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  addToBagModalOpenVar(true);

  const closeModal = (position: ModalCloseType) => {
    onClose();
    track({
      event: "Page Element Clicked",
      properties: {
        module_content: `ATB Confirmation: ${position}`,
        location_type: "ATB_CONFIRMATION"
      }
    });

    addToBagModalOpenVar(false);
    setConfirmation(false);
  };

  if (!hasMounted) return null;

  const isLargeDevice = isDesktop || isExtraLarge;

  return (
    <Drawer
      isOpen={confirmation}
      data-testid="ATBModalV2"
      isFullHeight={false}
      onClose={onClose}
      placement={isLargeDevice ? "right" : "bottom"}
      size="lg"
      onOverlayClick={() => {
        closeModal("Close Outside");
      }}
    >
      <DrawerOverlay />
      <DrawerContent
        padding="0 !important"
        maxW="34.375rem"
        data-testid="ATBModalContent"
        aria-label="Add To Bag Modal"
        height={isLargeDevice ? "100dvh" : "calc(100dvh - 64px)"}
      >
        <div className={titleContainer}>
          <SuccessIcon />
          {/* eslint-disable-next-line react/forbid-elements */}
          <h3 className={title}>Added To Basket</h3>
        </div>

        <DrawerCloseButton
          onClick={() => {
            closeModal("Close Icon");
          }}
          ml="-24px" // Those styles are for the close button on drawer that has position absolute
          right="2"
          top="1.5"
        />

        <DrawerBody px={0} pt={0} overflowX="hidden" data-testid="ATBModalBody">
          <ConfirmationPage
            variant={variant}
            variants={variants}
            showFreeTrial={isFreeTrialVariant}
          />
        </DrawerBody>
        <DrawerFooter
          display="flex"
          justifyContent="center"
          flexDirection="column"
          boxShadow={shadows.bottom.l1}
          px={2}
          position="relative"
        >
          {showProductAddedBanner && <ProductAddedBanner />}
          <DrawerFooterWrapper closeDrawer={closeModal} />
        </DrawerFooter>
        <DeprecatedDrawerLoadingMask showMask={isATBLoading} />
      </DrawerContent>
    </Drawer>
  );
};

export const ATBConfirmation = withErrorBoundary(ATBConfirmationComp, {
  fallback: <div />,
  onError(error) {
    Logger.warn(`ATB Confirmation failed to load. ${error}`);
  }
});
